import React from 'react';
import { Box, Typography, Stack, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { PriorityItem } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import DriverPill from '../DriverPill';
import PriorityChip from '../PriorityChip';
import WhyAffordance from '../ai/WhyAffordance';
import { WorkItemTypeBadge, workItemTypeHint } from '../workItems/WorkItemTypeTreatment';
import type { RationaleEntry } from '../../utils/aiRationale';
import { dashboardCopy } from '../../copy/dashboard';

// ---------------------------------------------------------------------------
// PriorityList — three-at-most list of what matters today. Each row carries
// a driver pill so strategic context is visible without a drill-down, plus
// a "Why?" affordance showing the AI reasoning behind the ordering when
// available.
// ---------------------------------------------------------------------------

interface PriorityListProps {
  items: PriorityItem[];
  loading?: boolean;
  /** workItemId → { rationale, strategyLink }. Optional — rows without an
   *  entry simply hide the affordance. */
  rationaleMap?: Map<string, RationaleEntry>;
}

export function PriorityList({ items, loading = false, rationaleMap }: PriorityListProps) {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600, mb: 1.5, display: 'block' }}
      >
        {dashboardCopy.prioritiesHeader}
      </Typography>

      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No priorities set for today.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Box
              key={item.workItem.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/work-items?id=${item.workItem.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/work-items?id=${item.workItem.id}`);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              {/* Scheduled time (tabular so columns align down the list). */}
              <Box
                sx={{
                  minWidth: 52,
                  fontSize: 13,
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 500,
                }}
              >
                {item.scheduledTime ?? '—'}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <WorkItemTypeBadge type={item.workItem.type} variant="compact" />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {item.workItem.title}
                  </Typography>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                    <WhyAffordance
                      rationale={rationaleMap?.get(item.workItem.id)?.rationale}
                      secondary={rationaleMap?.get(item.workItem.id)?.strategyLink}
                      size="compact"
                      label="Why this priority"
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <DriverPill driver={item.workItem.strategicDriver} size="compact" />
                  {item.plannedMinutes !== undefined && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatMinutes(item.plannedMinutes)}
                    </Typography>
                  )}
                  {(() => {
                    const hint = workItemTypeHint(item.workItem);
                    return hint ? (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {hint}
                      </Typography>
                    ) : null;
                  })()}
                </Stack>
              </Box>

              <PriorityChip priority={item.workItem.priority} />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default PriorityList;
